import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ChevronRight,
  Clock,
  BookOpen,
  Sparkles,
  Heart,
  ShieldCheck,
  Lightbulb,
  Mic,
  CheckCircle2,
  XCircle,
  ArrowLeft,
  Trophy,
} from 'lucide-react';
import { useT } from '../../context/LanguageContext';
import { VocabularyMeaningList, MeaningToggleButton, MeaningBlock } from '../../components/i18n/VocabularyMeaning';

const VOCAB = [
  { fr: 'immeuble', note: 'grand bâtiment où vivent plusieurs familles', en: 'apartment building — a large building where several families live' },
  { fr: 'voisin', note: 'personne qui habite près de chez nous', en: 'neighbour — a person who lives near us' },
  { fr: 'ascenseur', note: 'appareil pour monter ou descendre dans un immeuble', en: 'elevator — device to go up or down in a building' },
  { fr: 'sac', note: 'objet utilisé pour transporter des choses', en: 'bag — an object used to carry things' },
  { fr: 'lourd', note: 'qui pèse beaucoup', en: 'heavy — weighs a lot' },
  { fr: 'jardin', note: 'espace avec des plantes et des fleurs', en: 'garden — a space with plants and flowers' },
  { fr: 'vacances', note: 'période de repos', en: 'holidays — a period of rest' },
  { fr: 'frapper à la porte', note: 'faire du bruit sur une porte', en: 'to knock on the door — to make a sound on a door' },
  { fr: 'surprise', note: 'sentiment quand quelque chose arrive sans prévenir', en: 'surprise — a feeling when something happens unexpectedly' },
  { fr: 'amie', note: "personne avec qui on partage de bons moments", en: 'friend (fem.) — a person with whom we share good moments' },
];

const COMPREHENSION = [
  { fr: 'Où habite Emma ?', en: 'Where does Emma live?' },
  { fr: 'Avec qui habite-t-elle ?', en: 'Who does she live with?' },
  { fr: "Comment s'appelle la nouvelle amie d'Emma ?", en: "What is Emma's new friend called?" },
  { fr: 'Pourquoi Sarah aide-t-elle Emma ?', en: 'Why does Sarah help Emma?' },
  { fr: "Quelle leçon Emma apprend-elle à la fin de l'histoire ?", en: 'What lesson does Emma learn at the end of the story?' },
];

const TRUE_FALSE = [
  { q: { fr: 'Emma habite avec sa mère.', en: 'Emma lives with her mother.' }, a: false },
  { q: { fr: 'Sarah vit dans le même immeuble.', en: 'Sarah lives in the same building.' }, a: true },
  { q: { fr: "L'ascenseur fonctionne bien le jour de leur rencontre.", en: 'The elevator works well on the day they meet.' }, a: false },
  { q: { fr: 'Sarah part en vacances pendant trois semaines.', en: 'Sarah goes on holiday for three weeks.' }, a: true },
  { q: { fr: 'Emma comprend que certaines amitiés peuvent durer longtemps.', en: 'Emma understands that some friendships can last a long time.' }, a: true },
];

const REACT_QS = [
  { fr: 'As-tu un meilleur ami ou une meilleure amie ?', en: 'Do you have a best friend?' },
  { fr: "Pourquoi l'amitié est-elle importante ?", en: 'Why is friendship important?' },
  { fr: 'Aimes-tu aider les autres ? Pourquoi ?', en: 'Do you like helping others? Why?' },
];

export default function MaNouvelleAmie() {
  const t = useT();
  const [tfAnswers, setTfAnswers] = useState({});
  const [reactNotes, setReactNotes] = useState({});
  const [defi, setDefi] = useState('');
  const [completed, setCompleted] = useState(false);

  const tfScore = TRUE_FALSE.reduce(
    (acc, item, i) => acc + (tfAnswers[i] === item.a ? 1 : 0),
    0
  );

  return (
    <div className="min-h-screen bg-[#FAF7F0]" data-testid="story-ma-nouvelle-amie">
      {/* TOP NAV */}
      <section className="pt-12 pb-6">
        <div className="max-w-[860px] mx-auto px-6">
          <nav className="text-[12px] text-[#6B4E37] mb-6 flex items-center gap-1.5 flex-wrap" data-testid="breadcrumb">
            <Link to="/" className="hover:text-[#D4AF37] transition-colors">Selora</Link>
            <ChevronRight className="w-3 h-3 text-[#D4AF37]" />
            <Link to="/lire-le-francais" className="hover:text-[#D4AF37] transition-colors">Lire le français</Link>
            <ChevronRight className="w-3 h-3 text-[#D4AF37]" />
            <Link to="/lire-le-francais/lire-et-comprendre/a1" className="hover:text-[#D4AF37] transition-colors">Lire &amp; Comprendre</Link>
            <ChevronRight className="w-3 h-3 text-[#D4AF37]" />
            <span className="hover:text-[#D4AF37] transition-colors">A1</span>
            <ChevronRight className="w-3 h-3 text-[#D4AF37]" />
            <span className="hover:text-[#D4AF37] transition-colors">Histoires</span>
            <ChevronRight className="w-3 h-3 text-[#D4AF37]" />
            <span className="text-[#1A2F4A] font-semibold">Ma nouvelle amie</span>
          </nav>

          <Link
            to="/lire-le-francais/lire-et-comprendre/a1"
            className="inline-flex items-center gap-2 text-[12px] text-[#6B4E37] hover:text-[#D4AF37] transition-colors mb-2"
            data-testid="back-to-a1">
            <ArrowLeft className="w-3.5 h-3.5" />
            {t({ fr: 'Retour aux histoires A1', en: 'Back to A1 stories' })}
          </Link>
        </div>
      </section>

      {/* HERO */}
      <section className="pb-12">
        <div className="max-w-[860px] mx-auto px-6">
          <div className="bg-white border border-[#E8E4DC] rounded-2xl overflow-hidden" data-testid="story-hero">
            {/* Hero image */}
            <div className="relative h-72 sm:h-96 bg-gradient-to-br from-[#FAF7F0] via-[#FFF8E7] to-[#D4AF37]/15 overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1529390079861-591de354faf5?auto=format&fit=crop&w=1400&q=80"
                alt="Deux amies souriant devant un immeuble"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/35 via-transparent to-transparent" />
              <div className="absolute top-4 left-4 flex items-center gap-2 flex-wrap">
                <span className="bg-[#1A2F4A] text-[#D4AF37] text-[10px] font-bold px-3 py-1 rounded-full tracking-widest">A1</span>
                <span className="bg-white/95 text-[#1A2F4A] text-[10px] font-semibold px-3 py-1 rounded-full flex items-center gap-1">
                  <Clock className="w-3 h-3" /> {t({ fr: '2 minutes', en: '2 minutes' })}
                </span>
                <span className="bg-[#D4AF37]/95 text-[#1A2F4A] text-[10px] font-bold px-3 py-1 rounded-full flex items-center gap-1 tracking-wider">
                  <span aria-hidden="true">✦</span> Création Originale Selora
                </span>
              </div>
            </div>

            <div className="px-7 sm:px-10 py-8 text-center">
              <p className="text-[10px] text-[#D4AF37] uppercase tracking-[0.3em] font-bold mb-2">{t({ fr: "Histoire A1 · L'amitié", en: 'A1 story · Friendship' })}</p>
              <h1
                className="text-4xl sm:text-5xl font-bold text-[#1A2F4A] leading-tight"
                style={{ fontFamily: "'Playfair Display', serif" }}
                data-testid="story-title">
                Ma nouvelle amie
              </h1>
              <div className="flex items-center justify-center gap-3 mt-5">
                <div className="h-px w-8 bg-[#D4AF37]/50" />
                <BookOpen className="w-4 h-4 text-[#D4AF37]" />
                <div className="h-px w-8 bg-[#D4AF37]/50" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* STORY TEXT */}
      <section className="pb-14" data-testid="story-text-section">
        <div className="max-w-[720px] mx-auto px-6">
          <div className="bg-white border border-[#E8E4DC] rounded-2xl px-7 sm:px-10 py-8 sm:py-10 text-[#1A2F4A] leading-[1.85] text-[16px] space-y-4" style={{ fontFamily: "'Playfair Display', serif" }}>
            <p>Emma a quinze ans. Elle habite dans un grand immeuble en Belgique avec son père.</p>
            <p>Quand Emma était petite, sa mère a quitté la maison. Depuis ce jour, Emma est souvent triste.</p>
            <p>Elle pense :</p>
            <p className="pl-6 italic text-[#6B4E37]">« Les amis partent toujours. »</p>
            <p>Alors, Emma ne veut pas avoir beaucoup d'amis. Elle parle avec les voisins, mais elle ne parle pas beaucoup.</p>
            <p>Dans le même immeuble, il y a une fille qui s'appelle Sarah. Elle a le même âge qu'Emma.</p>
            <p>Sarah est gentille et souriante.</p>
            <p>Chaque matin, Sarah dit :</p>
            <p className="pl-6 italic text-[#6B4E37]">— Bonjour, Emma !</p>
            <p>Emma répond :</p>
            <p className="pl-6 italic text-[#6B4E37]">— Bonjour.</p>
            <p>Emma voit souvent Sarah dans l'ascenseur ou devant l'immeuble.</p>
            <p>Un jour, l'ascenseur est en panne. Emma porte un sac très lourd.</p>
            <p>Sarah arrive.</p>
            <p className="pl-6 italic text-[#6B4E37]">— Je peux t'aider ?</p>
            <p className="pl-6 italic text-[#6B4E37]">— Oui, merci.</p>
            <p>Les deux filles montent les escaliers ensemble.</p>
            <p>Après ce jour, elles parlent un peu plus. Elles passent du temps dans le jardin de l'immeuble. Elles promènent parfois le chien du voisin. Elles achètent une glace après l'école.</p>
            <p>Emma aime beaucoup ces moments.</p>
            <p>Un été, Sarah part en vacances pendant trois semaines.</p>
            <p>Emma pense :</p>
            <p className="pl-6 italic text-[#6B4E37]">« Voilà. Comme tout le monde, elle va partir. »</p>
            <p>Mais trois semaines plus tard, Sarah revient.</p>
            <p>Elle frappe à la porte d'Emma.</p>
            <p className="pl-6 italic text-[#6B4E37]">— Salut ! Tu m'as manqué !</p>
            <p>Emma est surprise.</p>
            <p>Les mois passent. Puis une année.</p>
            <p>Sarah est toujours là.</p>
            <p>Un soir, Emma parle avec son père.</p>
            <p className="pl-6 italic text-[#6B4E37]">— Papa, je pensais que tous les amis partaient.</p>
            <p>Son père sourit.</p>
            <p className="pl-6 italic text-[#6B4E37]">— Oui, certaines personnes partent. Mais d'autres restent près de nous.</p>
            <p>Le lendemain, Emma retrouve Sarah dans le jardin.</p>
            <p className="pl-6 italic text-[#6B4E37]">— Merci d'être mon amie, dit Emma.</p>
            <p>Sarah sourit.</p>
            <p className="pl-6 italic text-[#6B4E37]">— Toujours.</p>
            <p>Emma comprend alors quelque chose d'important :</p>
            <p className="text-center text-lg italic text-[#1A2F4A] py-3">Toutes les personnes ne sont pas les mêmes.</p>
            <p>Certaines personnes partent, mais d'autres restent et deviennent de vrais amis.</p>
          </div>
        </div>
      </section>

      {/* VOCABULAIRE */}
      <section className="pb-14" data-testid="vocab-section">
        <div className="max-w-[860px] mx-auto px-6">
          <SectionHeader eyebrow={t({ fr: 'Vocabulaire', en: 'Vocabulary' })} icon={Sparkles}>
            {t({ fr: 'Vocabulaire', en: 'Vocabulary' })}
          </SectionHeader>
          <VocabularyMeaningList items={VOCAB} testid="vocab-list" />
        </div>
      </section>

      {/* COMPRÉHENSION */}
      <section className="pb-14" data-testid="comprehension-section">
        <div className="max-w-[760px] mx-auto px-6">
          <div className="flex items-center justify-between gap-3 mb-6 flex-wrap">
            <SectionHeader eyebrow={t({ fr: 'Compréhension', en: 'Comprehension' })} icon={Lightbulb}>
              {t({ fr: 'Compréhension', en: 'Comprehension' })}
            </SectionHeader>
            <MeaningToggleButton />
          </div>
          <div className="space-y-3">
            {COMPREHENSION.map((q, i) => (
              <div key={i} className="bg-white border border-[#E8E4DC] rounded-xl px-5 py-4" data-testid={`comp-q-${i}`}>
                <MeaningBlock fr={
                  <span className="text-[14px] text-[#1A2F4A] leading-relaxed flex items-start gap-2">
                    <span className="w-6 h-6 rounded-full bg-[#FAF7F0] border border-[#D4AF37]/40 flex items-center justify-center text-[#D4AF37] text-[11px] font-bold flex-shrink-0">{i + 1}</span>
                    {q.fr}
                  </span>
                } en={q.en} />
                <textarea
                  rows={2}
                  className="w-full mt-3 px-3 py-2 border border-[#E8E4DC] rounded-lg text-[13px] text-[#1A2F4A] bg-[#FAFAF8] focus:outline-none focus:border-[#D4AF37] resize-none"
                  placeholder={t({ fr: 'Votre réponse…', en: 'Your answer…' })}
                  data-testid={`comp-input-${i}`}
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* VRAI OU FAUX */}
      <section className="pb-14" data-testid="truefalse-section">
        <div className="max-w-[760px] mx-auto px-6">
          <SectionHeader eyebrow={t({ fr: 'Activité', en: 'Activity' })} icon={CheckCircle2}>
            {t({ fr: 'Vrai ou Faux', en: 'True or False' })}
          </SectionHeader>
          <div className="space-y-3">
            {TRUE_FALSE.map((item, i) => {
              const selected = tfAnswers[i];
              const isCorrect = selected === item.a;
              return (
                <div key={i} className="bg-white border border-[#E8E4DC] rounded-xl px-5 py-4" data-testid={`tf-${i}`}>
                  <MeaningBlock fr={
                    <span className="text-[14px] text-[#1A2F4A] leading-relaxed flex items-start gap-2">
                      <span className="w-6 h-6 rounded-full bg-[#FAF7F0] border border-[#D4AF37]/40 flex items-center justify-center text-[#D4AF37] text-[11px] font-bold flex-shrink-0">{i + 1}</span>
                      {item.q.fr}
                    </span>
                  } en={item.q.en} className="mb-3" />
                  <div className="flex gap-2">
                    {[
                      { label: t({ fr: 'Vrai', en: 'True' }), key: 'vrai', value: true },
                      { label: t({ fr: 'Faux', en: 'False' }), key: 'faux', value: false },
                    ].map((opt) => {
                      const isPicked = selected === opt.value;
                      const isPickedCorrect = isPicked && isCorrect;
                      const isPickedWrong = isPicked && !isCorrect;
                      return (
                        <button
                          key={opt.key}
                          onClick={() => setTfAnswers((p) => ({ ...p, [i]: opt.value }))}
                          className={`px-4 py-1.5 rounded-full text-[12px] font-bold border transition-all ${
                            isPickedCorrect ? 'bg-green-50 border-green-300 text-green-700'
                              : isPickedWrong ? 'bg-red-50 border-red-300 text-red-700'
                                : 'bg-[#FAF7F0] border-[#E8E4DC] text-[#1A2F4A] hover:border-[#D4AF37]'
                          }`}
                          data-testid={`tf-${i}-${opt.key}`}>
                          {opt.label}
                        </button>
                      );
                    })}
                    {selected !== undefined && (
                      <span className="ml-auto inline-flex items-center gap-1 text-[11px] font-semibold">
                        {isCorrect ? (
                          <span className="text-green-700 flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> {t({ fr: 'Bonne réponse', en: 'Correct answer' })}</span>
                        ) : (
                          <span className="text-red-700 flex items-center gap-1"><XCircle className="w-3.5 h-3.5" /> {t({ fr: 'Essayez encore', en: 'Try again' })}</span>
                        )}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {Object.keys(tfAnswers).length === TRUE_FALSE.length && (
            <div className="mt-4 bg-[#1A2F4A] text-white rounded-xl px-5 py-3 flex items-center justify-between" data-testid="tf-score">
              <span className="text-[13px] font-semibold">{t({ fr: 'Votre score', en: 'Your score' })}</span>
              <span className="text-base font-bold text-[#D4AF37]">{tfScore} / {TRUE_FALSE.length}</span>
            </div>
          )}
        </div>
      </section>

      {/* RÉAGISSEZ */}
      <section className="pb-14" data-testid="react-section">
        <div className="max-w-[760px] mx-auto px-6">
          <SectionHeader eyebrow={t({ fr: 'Expression', en: 'Expression' })} icon={Heart}>
            {t({ fr: 'Réagissez !', en: 'React!' })}
          </SectionHeader>
          <div className="space-y-3">
            {REACT_QS.map((q, i) => (
              <div key={i} className="bg-white border border-[#E8E4DC] rounded-xl px-5 py-4" data-testid={`react-q-${i}`}>
                <MeaningBlock fr={<span className="text-[14px] text-[#1A2F4A] leading-relaxed">{q.fr}</span>} en={q.en} className="mb-3" />
                <textarea
                  rows={2}
                  value={reactNotes[i] || ''}
                  onChange={(e) => setReactNotes((p) => ({ ...p, [i]: e.target.value }))}
                  className="w-full px-3 py-2 border border-[#E8E4DC] rounded-lg text-[13px] text-[#1A2F4A] bg-[#FAFAF8] focus:outline-none focus:border-[#D4AF37] resize-none"
                  placeholder={t({ fr: 'Votre opinion…', en: 'Your opinion…' })}
                  data-testid={`react-input-${i}`}
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MINI DÉFI */}
      <section className="pb-14" data-testid="defi-section">
        <div className="max-w-[760px] mx-auto px-6">
          <SectionHeader eyebrow={t({ fr: 'Mini défi', en: 'Mini challenge' })} icon={Mic}>
            {t({ fr: 'Présentez votre ami(e)', en: 'Introduce your friend' })}
          </SectionHeader>
          <div className="bg-white border border-[#D4AF37]/40 rounded-2xl px-6 py-6">
            <MeaningBlock
              fr={<span className="text-[14px] text-[#1A2F4A] leading-relaxed">Présente ton meilleur ami ou ta meilleure amie en <strong>3 à 5 phrases</strong>.</span>}
              en="Introduce your best friend in 3 to 5 sentences."
              className="mb-3" />
            <p className="text-[12px] text-[#6B4E37] mb-3">{t({ fr: 'Tu peux parler de :', en: 'You can talk about:' })}</p>
            <ul className="grid sm:grid-cols-2 gap-y-1.5 gap-x-5 text-[12px] text-[#1A2F4A]/85 mb-4">
              {[
                { fr: 'son prénom', en: 'their first name' },
                { fr: 'son âge', en: 'their age' },
                { fr: 'sa personnalité', en: 'their personality' },
                { fr: 'ce que vous aimez faire ensemble', en: 'what you like to do together' },
              ].map((it) => (
                <li key={it.fr} className="flex items-start gap-2">
                  <span className="w-1 h-1 rounded-full bg-[#D4AF37] mt-2 flex-shrink-0" />
                  {t(it)}
                </li>
              ))}
            </ul>
            <textarea
              rows={4}
              value={defi}
              onChange={(e) => setDefi(e.target.value)}
              className="w-full px-3 py-2 border border-[#E8E4DC] rounded-lg text-[13px] text-[#1A2F4A] bg-[#FAFAF8] focus:outline-none focus:border-[#D4AF37] resize-none"
              placeholder={t({ fr: 'Écris ou enregistre ta présentation…', en: 'Write or record your introduction…' })}
              data-testid="defi-input"
            />
          </div>
        </div>
      </section>

      {/* MORALE */}
      <section className="pb-14" data-testid="morale-section">
        <div className="max-w-[760px] mx-auto px-6">
          <div className="bg-gradient-to-br from-[#1A2F4A] to-[#243a58] rounded-2xl px-7 py-7 text-white relative overflow-hidden">
            <div className="absolute -top-10 -right-10 w-40 h-40 bg-[#D4AF37]/10 rounded-full" aria-hidden="true" />
            <div className="relative">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-11 h-11 rounded-full bg-[#D4AF37]/15 border border-[#D4AF37]/40 flex items-center justify-center flex-shrink-0">
                  <ShieldCheck className="w-5 h-5 text-[#D4AF37]" />
                </div>
                <div>
                  <p className="text-[10px] text-[#D4AF37] uppercase tracking-[0.3em] font-bold">{t({ fr: 'Morale', en: 'Moral' })}</p>
                  <h3 className="text-xl font-bold leading-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
                    {t({ fr: "Ce que l'histoire nous apprend", en: 'What the story teaches us' })}
                  </h3>
                </div>
              </div>
              <p className="text-[14px] text-white/90 leading-relaxed mb-2">
                {t({
                  fr: "Une expérience triste ne doit pas nous empêcher de faire confiance aux autres.",
                  en: 'A sad experience should not stop us from trusting others.',
                })}
              </p>
              <p className="text-[14px] text-white/90 leading-relaxed italic" style={{ fontFamily: "'Playfair Display', serif" }}>
                {t({ fr: 'Les vraies amitiés peuvent durer longtemps.', en: 'True friendships can last a long time.' })}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* COMPLETION */}
      <section className="pb-20" data-testid="completion-section">
        <div className="max-w-[760px] mx-auto px-6">
          {!completed ? (
            <button
              onClick={() => setCompleted(true)}
              className="w-full bg-[#1A2F4A] hover:bg-[#243a58] text-white py-4 rounded-xl text-sm font-semibold transition-all flex items-center justify-center gap-2"
              data-testid="complete-btn">
              <Trophy className="w-4 h-4 text-[#D4AF37]" />
              {t({ fr: "J'ai terminé cette histoire", en: 'I have finished this story' })}
            </button>
          ) : (
            <div className="bg-white border border-[#D4AF37]/50 rounded-2xl px-7 py-8 text-center relative overflow-hidden" data-testid="completion-msg">
              <div className="absolute -top-10 -right-10 w-32 h-32 bg-[#D4AF37]/10 rounded-full" aria-hidden="true" />
              <div className="relative">
                <div className="text-4xl mb-3" aria-hidden="true">🎉</div>
                <h3 className="text-2xl font-bold text-[#1A2F4A] mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                  {t({ fr: 'Bravo !', en: 'Well done!' })}
                </h3>
                <p className="text-[14px] text-[#1A2F4A]/85 mb-2">{t({ fr: 'Vous avez terminé cette histoire.', en: 'You have finished this story.' })}</p>
                <p className="text-[13px] text-[#6B4E37] mb-5">
                  {t({
                    fr: "Continuez votre aventure dans Lire & Comprendre pour découvrir d'autres histoires originales de Selora.",
                    en: 'Continue your journey in Lire & Comprendre to discover more original Selora stories.',
                  })}
                </p>
                <Link
                  to="/lire-le-francais/lire-et-comprendre/a1"
                  className="inline-flex items-center gap-2 bg-[#D4AF37] hover:bg-[#C9A961] text-[#1A2F4A] px-6 py-3 rounded-full text-sm font-bold transition-all"
                  data-testid="back-to-a1-cta">
                  {t({ fr: 'Découvrir les autres histoires', en: 'Discover other stories' })}
                  <ChevronRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

function SectionHeader({ eyebrow, icon: Icon, children }) {
  return (
    <div className="mb-6">
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-4 h-4 text-[#D4AF37]" />
        <p className="text-[10px] text-[#D4AF37] uppercase tracking-[0.3em] font-bold">{eyebrow}</p>
      </div>
      <h2 className="text-2xl sm:text-3xl font-bold text-[#1A2F4A]" style={{ fontFamily: "'Playfair Display', serif" }}>
        {children}
      </h2>
    </div>
  );
}
